import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  debounce, isEmpty, castArray, each, find,
} from 'lodash';
import {
  Search, AssignmentInd, PostAdd, ImageSearchRounded, FilterNone,
} from '@mui/icons-material';
import { TextField, Avatar, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { TagValueIcon } from 'components/TagValueSearch/TagValueIcon';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { FilterResult } from 'lib/types/filters';
import { TAG_TYPE_IDS, DEBOUNCE_TIME } from 'lib/constants';
import {
  clearQuery, defaultFilterKeys, toSlug, updateRouterQuery,
} from 'lib/utils/filterUtils';
import { useUpgradeModal } from 'components/UpgradeModal';
import { userIsProOrAdmin } from 'lib/helpers';
import { useRoles } from 'lib/contexts/rolesContext';
import { filterOptions, formatFilterResults, MINIMUM_INPUT_LENGTH } from './utils';

interface Props {
  filters: (FilterResult | string)[];
  placeholder?: string;
  apiPath?: string;
  useElasticSearch?: boolean;
  disabled?: boolean;
  useNaturalLanguage?: boolean;
  allowImageSearch?: boolean;
}

export const MultiFilter = ({
  filters,
  placeholder,
  apiPath = '/filters',
  useElasticSearch = false,
  disabled = false,
  useNaturalLanguage = false,
  allowImageSearch = false,
}: Props) => {
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FilterResult[]>([]);
  const [isCriteriaLoading, setIsCriteriaLoading] = useState(false);

  const isFetching = useRef(false);

  const API = useApi();
  const currentUser = useCurrentUser();
  const { showUpgradeModal } = useUpgradeModal();
  const allRoles = useRoles() || [];

  const filterPosts = useMemo(
    () => debounce(async (input, callback) => {
      try {
        // If user type the symbol, don't search yet
        if (input === '#' || input === '@') {
          return null;
        }

        // If the input has not enough characters, just return the keyword option
        if ((input || '').length < MINIMUM_INPUT_LENGTH) {
          callback([]);
        }

        const searchResults = await API.get(apiPath, { query: input }) as any;
        const formattedResults = formatFilterResults(searchResults, useElasticSearch);

        callback(formattedResults);
        return null;
      } catch (error) {
        isFetching.current = false;
        return null;
      }
    }, DEBOUNCE_TIME),
    [API, apiPath],
  );

  useEffect(() => {
    isFetching.current = true;
    let active = true;
    if (isEmpty(inputValue)) {
      setOptions([]);
      return undefined;
    }

    filterPosts(inputValue, (results: FilterResult[]) => {
      isFetching.current = false;

      if (active) {
        const keyword: FilterResult = {
          id: 0,
          label: inputValue,
          subLabel: 'keyword',
          query: 'keyword',
          relevancyScore: 1,
        };
        const criteriaSearch: FilterResult = {
          id: 2,
          label: `Posts matching "${inputValue}"`,
          subLabel: 'Explore posts',
          query: 'criteriaSearch',
          relevancyScore: 0,
        };
        const imageSearch: FilterResult = {
          id: 3,
          label: `Image content matching "${inputValue}"`,
          subLabel: 'Explore images',
          query: 'imageSearch',
          relevancyScore: 0,
          params: {
            imageSearch: inputValue,
            sort: 'relevance',
            creditedStatus: 'all',
          },
          isProOnly: true,
        };
        const baseOptions = [keyword];

        if (useNaturalLanguage) {
          baseOptions.push(criteriaSearch);
        }

        if (allowImageSearch) {
          baseOptions.push(imageSearch);
        }

        if (results) {
          if (inputValue.charAt(0) !== '@' && inputValue.charAt(0) !== '#') {
            setOptions([
              ...results,
              ...baseOptions,
            ]);
            return;
          }
          setOptions(results);
          return;
        }
        setOptions(baseOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue]);

  const renderOptionImg = (option: FilterResult) => {
    switch (option.query) {
    case 'users':
      return (<Avatar alt={option.label} src={option.imgUrl} />);
    case 'tags':
      return (<div className="mr-6"><TagValueIcon tagTypeId={TAG_TYPE_IDS.tag} fontSize="large" color="secondary" /></div>);
    case 'events':
      return (<div className="mr-6"><TagValueIcon tagTypeId={TAG_TYPE_IDS.event} fontSize="large" color="secondary" /></div>);
    case 'role':
      return (<div className="mr-6"><AssignmentInd fontSize="large" color="secondary" /></div>);
    case 'postingAccount':
      return (<div className="mr-6"><PostAdd fontSize="large" color="secondary" /></div>);
    case 'criteriaSearch':
      return (<div className="mr-6"><ImageSearchRounded fontSize="large" color="secondary" /></div>);
    case 'imageSearch':
      return (<div className="mr-6"><ImageSearchRounded fontSize="large" color="secondary" /></div>);
    case 'import':
      return (<div className="mr-1"><FilterNone fontSize="large" color="secondary" /></div>);
    default:
      return (<Search />);
    }
  };

  const renderSelectedLabel = (option: FilterResult | string) => {
    if (typeof option === 'string') {
      return `keyword: ${option}`;
    }

    switch (option.query) {
    case 'users':
      return `@${option.label}`;
    case 'keyword':
      return `keyword: ${option.label}`;
    case 'role':
      return `role: ${option.label}`;
    case 'postingAccount':
      return `account: ${option.label}`;
    case 'excludeUserId':
      return `NOT @${option.label}`;
    case 'excludeEventId':
      return `NOT #${option.label}`;
    case 'imageSearch':
      return `images: ${option.label}`;
    case 'importId':
      return `import: ${option.label}`;
    default: {
      return `${option.subLabel.substring(0, option.subLabel.length - 1)}: ${option.label}`;
    }
    }
  };

  const change = async (values: (FilterResult | string)[]) => {
    if (values.find((v) => (v as FilterResult)?.query === 'criteriaSearch')) {
      try {
        setIsCriteriaLoading(true);
        const response = await API.getPostSearchCriteria(inputValue);
        updateRouterQuery(router, response.params);
        return;
      } catch (error) {
        console.error('Error fetching post search criteria', error);
      } finally {
        setIsCriteriaLoading(false);
      }
      return;
    }

    // Check if imageSearch was removed
    const hadImageSearch = filters.some((f) => (f as FilterResult)?.query === 'imageSearch');
    const hasImageSearch = values.some((v) => (v as FilterResult)?.query === 'imageSearch');
    const buildMultiQuery = () => values.reduce(
      (acc, value) => {
        if (typeof value === 'string') {
          acc.keyword = value;
          return acc;
        }

        if (value.query === 'imageSearch') {
          acc.imageSearch = value.params?.imageSearch || value.label;
          if (value.params?.sort) {
            acc.sort = value.params?.sort;
          }
          if (value.params?.creditedStatus) {
            acc.creditedStatus = value.params?.creditedStatus;
          }
          return acc;
        }
        if (value.query === 'import') {
          return {
            importId: toSlug(value.label, value.id),
            creditedStatus: 'all',
          };
        }
        const key = value.query || 'keyword';
        const slug = toSlug(value.label, value.id);

        if (!acc[key]) {
          acc[key] = [slug];
        } else {
          acc[key].push(slug);
        }

        if (key === 'role' && allRoles.length && !castArray(router.query[key]).includes(slug)) {
          const fullRole = find(allRoles, (r) => r.id === value.id);
          if (fullRole?.relatedRoles) {
            each(fullRole.relatedRoles, (rr) => {
              const relatedSlug = toSlug(rr.name as string, rr.id);
              if (!acc[key]) {
                acc[key] = [relatedSlug];
              } else {
                acc[key].push(relatedSlug);
              }
            });
          }
        }

        return acc;
      }, {} as any,
    );

    if (hasImageSearch && !userIsProOrAdmin(currentUser)) {
      showUpgradeModal('Upgrade to Gondola PRO to search for social media posts by image content.');
      return;
    }

    const query = {
      ...clearQuery(defaultFilterKeys),
      ...buildMultiQuery(),
    };

    // Clear sort an creditedStatus if imageSearch was removed
    if (hadImageSearch && !hasImageSearch) {
      query.sort = [];
      query.creditedStatus = [];
    }

    updateRouterQuery(router, query);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      loading={!!inputValue?.length && (isFetching.current || isCriteriaLoading)}
      value={filters}
      filterOptions={filterOptions}
      className="mx-auto"
      autoHighlight
      getOptionLabel={(option) => renderSelectedLabel(option)}
      options={options}
      onChange={(_, v) => change(v)}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue.trim());
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder || 'Search creators/companies, tags, events, keywords, roles'}
          fullWidth
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <div className="absolute text-mediumgray right-4 top-3">
                {isCriteriaLoading ? <CircularProgress size={20} /> : null}
              </div>
            ),
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter' && (isFetching.current || isCriteriaLoading)) {
              e.stopPropagation();
            }
          }}
        />
      )}
      //@ts-ignore
      renderOption={
        (props, option: FilterResult) => (
          <li {...props} key={option.id}>
            <div className="flex w-full">
              <div className="w-20">
                {renderOptionImg(option)}
              </div>
              <div className="w-4/5">
                <div className="font-bold">
                  {option.label}
                </div>
                <div className="text-mediumgray text-sm">
                  {option.subLabel}
                </div>
              </div>
            </div>
          </li>
        )
      }
      size="small"
      disabled={disabled || isCriteriaLoading}
    />
  );
};
